import React, { useEffect, useState } from "react";
import classNames from "classnames";
import * as RadixSelect from "@radix-ui/react-select";
import type { SelectContentImplProps } from "@radix-ui/react-select";

import { ChevronIcon } from "components/icons/icons";

import { useReserveGapForFixedElements } from "./hooks/useReserveGapForFixedElements";

type SelectProps = {
  /**
   * Value of select
   * Should be set as undefined initially to display placeholder
   */
  value: string | undefined;
  setValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  width: number;
  placeholder: string;
  options: { value: string; label: string }[];
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Can be used to hook into state changes for purposes such as tracking
   * @param arg
   */
  onValueChange?: (arg: string) => Promise<void>;
  position?: SelectContentImplProps["position"];
  open?: boolean;
  defaultOpen?: boolean;
};

const Select = ({
  width,
  placeholder,
  options,
  ariaLabel,
  value = undefined,
  setValue,
  disabled,
  onValueChange,
  open: isSelectOpen,
  defaultOpen,
}: SelectProps) => {
  const [open, setOpen] = useState(isSelectOpen);

  useReserveGapForFixedElements({ modalOpened: !!open });

  useEffect(() => {
    setOpen(isSelectOpen);
  }, [isSelectOpen]);

  return (
    <RadixSelect.Root
      value={value}
      onValueChange={async (newValue) => {
        if (onValueChange) await onValueChange(newValue);
        setValue(newValue);
      }}
      disabled={disabled}
      open={open}
      onOpenChange={(open) => setOpen(open)}
      defaultOpen={defaultOpen}
    >
      <RadixSelect.Trigger
        className={classNames(
          "group inline-flex h-[50px] items-center justify-between border border-[#D0D0D6] bg-white px-[15px] text-[16px] leading-[130%] text-black-rock",
          "data-[placeholder]:font-normal data-[placeholder]:text-mono-40",
          "disabled:cursor-not-allowed disabled:bg-[#E3E3E5] disabled:text-mono-30 disabled:data-[placeholder]:text-mono-30",
          "focus-visible:inset focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black-rock"
        )}
        aria-label={ariaLabel}
        style={{ width }}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon
          className={classNames(
            "text-black-rock transition-[transform] duration-300",
            "group-data-[state=open]:rotate-180",
            "group-disabled:text-[#AAAAB2]"
          )}
        >
          <ChevronIcon width={24} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className={
            "w-[var(--radix-select-trigger-width)] overflow-hidden bg-white text-[14px] text-black-rock"
          }
          position="popper"
        >
          <RadixSelect.Viewport
            className={"border border-t-0 border-[#D0D0D6]"}
          >
            {options.map((option, i) => {
              return (
                <RadixSelect.Item
                  key={i}
                  value={option.value}
                  className={classNames(
                    "flex select-none items-center border-t border-t-[#D0D0D6] py-[16px] pl-[14px] pr-[48px] text-[16px] leading-[130%] text-black-rock",
                    "data-[highlighted]:bg-[#f9f9f9] data-[highlighted]:outline-none",
                    "first:border-t-0 data-[state=checked]:bg-[#E6E6EB]"
                  )}
                >
                  <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                </RadixSelect.Item>
              );
            })}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

export default Select;
